/**
 * Forgiving aim for taps and drops on the board. A finger is wide and the squares are small: a
 * drop or a tap that lands just beside a lit square (a move, a capture, a hop, a pawn to choose)
 * means that square, as the light already told the player. The board marks the squares that can
 * be aimed at with `data-target`; the square a move starts from is never aimed past.
 */

/** How far beyond a lit square's edge a drop still reaches it, as a share of a square. */
export const DROP_REACH = 0.5;
/** The same for a tap on an empty square, which is otherwise a tap on nothing. */
export const TAP_REACH = 0.3;

interface Point {
  readonly x: number;
  readonly y: number;
}

const squareUnder = (p: Point): HTMLElement | null =>
  document.elementFromPoint(p.x, p.y)?.closest<HTMLElement>('[data-square]') ?? null;

/** Distance from a point to a rectangle (0 inside it). */
function distanceTo(p: Point, r: DOMRect): number {
  const dx = Math.max(r.left - p.x, 0, p.x - r.right);
  const dy = Math.max(r.top - p.y, 0, p.y - r.bottom);
  return Math.hypot(dx, dy);
}

/**
 * The square (its data-square name) meant by a drop or tap at `points`, tried in order (on a touch
 * screen, the dragged copy above the finger, then the finger itself): a lit square under one of
 * them; else the lit square nearest to any of them, within `reach` of a square's width beyond its
 * edge; else the square under the first point, or null off the board.
 */
export function snapSquare(points: readonly Point[], reach: number): string | null {
  const under = points.map(squareUnder);
  for (const el of under) if (el?.dataset.target !== undefined) return el.dataset.square ?? null;
  // Back over the square the move starts from: that is a way to let go of it, not a near miss.
  const first = under[0] ?? null;
  if (first?.dataset.origin !== undefined) return first.dataset.square ?? null;
  let best: { name: string; d: number } | null = null;
  for (const el of document.querySelectorAll<HTMLElement>('[data-square][data-target]')) {
    const r = el.getBoundingClientRect();
    const limit = r.width * reach;
    for (const p of points) {
      const d = distanceTo(p, r);
      if (d <= limit && (!best || d < best.d)) best = { name: el.dataset.square ?? '', d };
    }
  }
  return best?.name || (first?.dataset.square ?? null);
}
